export interface Neighborhood {
  id: string;
  name: string;
  emoji: string;
  description: string;
  transitTime: {
    walk?: string;
    bus?: string;
    light_rail?: string;
    drive?: string;
    bike?: string;
  };
  arrivalTip: string;  // What to do FIRST when you arrive
  firstStop: {
    name: string;
    description: string;
    type: 'eat' | 'see' | 'do';
  };
  entryLevelTips: string[];  // 2-3 tips for neighborhood newcomers
  imageClass: string;
  mapLink?: string;
}

export interface NeighborhoodCategory {
  title: string;
  emoji: string;
  neighborhoods: Neighborhood[];
}

export const neighborhoodCategories: NeighborhoodCategory[] = [
  {
    title: "Essential Seattle Neighborhoods",
    emoji: "🚇",
    neighborhoods: [
      {
        id: "downtown",
        name: "Pike Place & Pioneer Square",
        emoji: "🐟",
        description: "Seattle's iconic public market and waterfront destination. Flying fish, fresh produce, and the first Starbucks.",
        transitTime: {
          bus: "18 min",
          light_rail: "22 min",
          drive: "8 min",
          bike: "12 min"
        },
        arrivalTip: "Arrive early (before 10 AM) to beat crowds and see vendors setting up. The market is most magical in the morning light.",
        firstStop: {
          name: "Piroshky Piroshky",
          description: "Start with a warm Russian pastry from this legendary bakery. The smoked salmon pate piroshky is unforgettable.",
          type: "eat"
        },
        entryLevelTips: [
          "🚇 Take bus route 10 from Capitol Hill (18 min) or walk downhill (35 min scenic route)",
          "🎯 Don't miss the Gum Wall in Post Alley - quirky Seattle at its finest",
          "💡 The lower levels have hidden artisan shops - explore beyond the main arcade"
        ],
        imageClass: "pike-place-market",
        mapLink: "https://www.google.com/maps/dir//Pike+Place+Market"
      },
      {
        id: "capitol-hill",
        name: "Capitol Hill",
        emoji: "🏡",
        description: "Your home base! Seattle's most vibrant neighborhood with cafes, bars, parks, and LGBTQ+ culture.",
        transitTime: {
          walk: "Already here! 🎉"
        },
        arrivalTip: "Step outside and walk to Broadway - Capitol Hill's main artery - to get your bearings. You're 6 blocks east.",
        firstStop: {
          name: "Volunteer Park",
          description: "Walk north to this stunning 48-acre park with a historic water tower offering 360° city views (free!).",
          type: "see"
        },
        entryLevelTips: [
          "🌳 Your street is residential and quiet - Broadway is where the action happens",
          "☕ Cal Anderson Park is the neighborhood's living room - perfect for people watching",
          "🌈 Pike/Pine corridor (blocks 1000-1500) is the nightlife and restaurant hub"
        ],
        imageClass: "capitol-hill-street",
        mapLink: "https://www.google.com/maps/place/Capitol+Hill,+Seattle,+WA"
      },
      {
        id: "fremont",
        name: "Fremont",
        emoji: "🧌",
        description: "Quirky, artsy neighborhood known as the 'Center of the Universe' with public art and Sunday markets.",
        transitTime: {
          bus: "32 min",
          drive: "12 min",
          bike: "18 min"
        },
        arrivalTip: "Start at the Fremont Troll under the Aurora Bridge, then walk the neighborhood's art installations.",
        firstStop: {
          name: "Fremont Troll",
          description: "Visit this 18-foot sculpture crushing a VW Beetle under the bridge. Iconic photo op and neighborhood symbol.",
          type: "see"
        },
        entryLevelTips: [
          "🚌 Take bus 62 from Broadway & E John St (direct, no transfers)",
          "🎨 Walk the 'Fremont Art Walk' - Lenin statue, Rapunzel tower, Waiting for the Interurban sculpture",
          "🍔 Fremont Brewing (outdoor beer garden) or Paseo (Caribbean sandwiches) for lunch"
        ],
        imageClass: "fremont-troll",
        mapLink: "https://www.google.com/maps/dir//Fremont,+Seattle,+WA"
      },
      {
        id: "ballard",
        name: "Ballard",
        emoji: "🛶",
        description: "Historic Scandinavian fishing village turned trendy neighborhood with breweries and waterfront views.",
        transitTime: {
          bus: "42 min",
          drive: "15 min",
          bike: "25 min"
        },
        arrivalTip: "If visiting on Sunday, hit the Ballard Farmers Market (10 AM-3 PM) first - it's the best in Seattle.",
        firstStop: {
          name: "Ballard Locks",
          description: "Watch boats navigate between saltwater and freshwater, and see salmon climbing the fish ladder (peak: July-Sept).",
          type: "see"
        },
        entryLevelTips: [
          "🚌 Take bus 49 to U District, transfer to 44 (or drive - parking is easier here)",
          "🍺 Ballard has 10+ breweries - pick up a 'Ballard Brewery District' map at any taproom",
          "🌊 Walk the Burke-Gilman Trail along the water for stunning Puget Sound views"
        ],
        imageClass: "ballard-locks",
        mapLink: "https://www.google.com/maps/dir//Ballard,+Seattle,+WA"
      },
      {
        id: "university-district",
        name: "University District (UW)",
        emoji: "🎓",
        description: "Home to the University of Washington's stunning campus and the vibrant Ave (University Way).",
        transitTime: {
          bus: "25 min",
          light_rail: "28 min",
          drive: "10 min",
          bike: "15 min"
        },
        arrivalTip: "Enter campus via Red Square - the heart of UW - then walk to Drumheller Fountain for iconic Mt. Rainier views.",
        firstStop: {
          name: "UW Campus Cherry Blossoms",
          description: "Visit the Quad in late March/early April for world-famous cherry blossom viewing (or any time for beautiful architecture).",
          type: "see"
        },
        entryLevelTips: [
          "🚇 Light rail from Capitol Hill Station to U District Station (11 min) - most convenient",
          "📚 Suzzallo Library's reading room looks like Hogwarts - free to enter and stunning",
          "🍜 The Ave has cheap, delicious international food (Thai Tom, Shultzy's, Aladdin Gyro-Cery)"
        ],
        imageClass: "uw-campus",
        mapLink: "https://www.google.com/maps/dir//University+of+Washington,+Seattle,+WA"
      },
      {
        id: "lake-union",
        name: "Downtown / Waterfront",
        emoji: "🌊",
        description: "Seattle's urban core with skyscrapers, shopping, and waterfront attractions along Elliott Bay.",
        transitTime: {
          bus: "22 min",
          light_rail: "15 min",
          drive: "10 min"
        },
        arrivalTip: "Take the light rail to Westlake Station, then walk downhill to the waterfront via Pike Place Market.",
        firstStop: {
          name: "Seattle Great Wheel",
          description: "Ride the Ferris wheel on Pier 57 for panoramic Elliott Bay views - especially beautiful at sunset.",
          type: "do"
        },
        entryLevelTips: [
          "🚇 Capitol Hill → Westlake via light rail (8 min, $3) - fastest and easiest",
          "🏙️ Downtown is walkable, but hills are steep - wear comfortable shoes",
          "🚢 The waterfront renovation (2019-2024) added new parks, piers, and the Overlook Walk"
        ],
        imageClass: "seattle-waterfront",
        mapLink: "https://www.google.com/maps/dir//Seattle+Waterfront"
      },
      {
        id: "queen-anne",
        name: "Queen Anne / Seattle Center",
        emoji: "🗼",
        description: "Home to the Space Needle, museums, and the quieter residential slopes of Queen Anne Hill.",
        transitTime: {
          bus: "28 min",
          light_rail: "18 min",
          drive: "8 min",
          bike: "14 min"
        },
        arrivalTip: "Don't just do the Space Needle! Visit Seattle Center's grounds first (free) to see the International Fountain and MoPOP.",
        firstStop: {
          name: "Kerry Park (Upper Queen Anne)",
          description: "Take the iconic Seattle skyline photo from this tiny park on the hillside. Go at sunset or blue hour.",
          type: "see"
        },
        entryLevelTips: [
          "🚇 Light rail to Westlake, then the Monorail to Seattle Center (separate fare, ORCA accepted) — about 15 min total",
          "🗼 Space Needle tickets are expensive ($35-40) - consider Seattle City Pass if doing multiple attractions",
          "☕ Top Pot Doughnuts in Queen Anne is worth the climb up the hill"
        ],
        imageClass: "space-needle-kerry-park",
        mapLink: "https://www.google.com/maps/dir//Kerry+Park,+Seattle,+WA"
      }
    ]
  }
];
